<template>
    <div id="commContainer">
        <div id="commTabs">
            <div class="commTabItem button" @click="setTab('twitter')" :class="{ commTabActive: tab === 'twitter' }">
                <i class="fab fa-twitter commTabIcon" />
                <div class="unreadCount" v-show="unviewed('twitter') > 0">{{ unviewed('twitter') }}</div>
            </div>
            <div class="commTabItem button" @click="setTab('facebook')" :class="{ commTabActive: tab === 'facebook' }">
                <i class="fab fa-facebook-square commTabIcon" style="font-size: 1.1em" />
                <div class="unreadCount" v-show="unviewed('facebook') > 0">{{ unviewed('facebook') }}</div>
            </div>
            <div class="commTabItem button" @click="setTab('email')" :class="{ commTabActive: tab === 'email' }">
                <i class="fas fa-envelope commTabIcon" />
                <div class="unreadCount" v-show="unviewed('email') > 0">{{ unviewed('email') }}</div>
            </div>
        </div>

        <div class="commContent" :class="{ participant: !gamemaster, dashboard: gamemaster }" v-if="tab == 'twitter'">
            <div class="commContentFeed">
                <Tweet v-for="tweet in tweets" :key="tweet.post_id" :publishTime="tweet.publish_time_ts"
                    :edit="gamemaster" :authorName="tweet.account_name" :authorHandle="tweet.account_handle"
                    :imageUrl="tweet.image_url" v-bind="tweet" @removePost="removePost" @replyTweet="replyPost" />
            </div>

            <div class="messageContainer" v-if="tweets.length == 0" style="z-index: 1">
                <div class="tlMessage">
                    <h1>Twitter feed</h1>
                    <h2>{{ $t("communication.noNewTweets") }}</h2>
                </div>
            </div>
        </div>

        <div class="commContent" :class="{ participant: !gamemaster, dashboard: gamemaster }" v-if="tab == 'facebook'">
            <div class="commContentFeed">
                <FacebookPost v-for="fb in facebook_posts" :key="fb.post_id" :publishTime="fb.publish_time_ts"
                    :authorName="fb.account_name" :authorHandle="fb.account_handle" :imageUrl="fb.image_url"
                    :edit="gamemaster" v-bind="fb" @removePost="removePost" @refreshGenUser="refreshGenUser" />
            </div>

            <div class="messageContainer" v-if="facebook_posts.length == 0" style="z-index: 1">
                <div class="tlMessage">
                    <h1>Facebook feed</h1>
                    <h2>{{ $t("communication.noNewFb") }}</h2>
                </div>
            </div>
        </div>

        <div class="commContent" :class="{ participant: !gamemaster, dashboard: gamemaster }" v-if="tab == 'email'">
            <div class="commContentFeed">
                <Email v-for="email in emails" :key="email.post_id" :publishTime="email.publish_time_ts"
                    :authorName="email.account_name" :authorHandle="email.account_handle" :imageUrl="email.image_url"
                    :edit="gamemaster" v-bind="email" @removePost="removePost" />
            </div>

            <div class="messageContainer" v-if="emails.length == 0" style="z-index: 1">
                <div class="tlMessage">
                    <h1>Email feed</h1>
                    <h2>{{ $t("communication.noNewEmails") }}</h2>
                </div>
            </div>
        </div>

        <div class="commContent" :class="{ participant: !gamemaster, dashboard: gamemaster }" v-if="tab == 'upload'">
        </div>

        <PostCompose v-show="tab != 'upload'" ref="postCompose" :instance_id="instance_id" :compact="true"
            :gamemaster="gamemaster" :pgroup_name="pgroup_name" :pgroup_id="pgroup_id"
            @selectedAccount="selectedAccount" />

        <div id="connStatus" v-if="!gamemaster">
            <i class="fas fa-circle" :style="{ color: connected ? '#40c240' : '#e3483d' }" id="connIcon" />
            <div id="connText"> {{ $t("common.systemStatus", { status: connected ? 'online' : 'offline' }) }}</div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Email from '../components/Email';
import FacebookPost from '../components/FacebookPost';
import PostCompose from '../components/PostCompose';
import Tweet from '../components/Tweet';

import Vue from 'vue';
import VueI18n from 'vue-i18n';

Vue.use(VueI18n);

export default {
    name: 'Communication',
    components: {
        Tweet,
        PostCompose,
        FacebookPost,
        Email
    },
    props: {
        instance_id: Number,
        pgroup_id: Number,
        pgroup_name: String,
        gamemaster: Boolean
    },
    data() {
        return {
            tab: '',
            selected_account: null,
            viewed: []
        }
    },
    computed: {
        ...mapGetters({
            posts: 'getPubPosts',
            pgroups: 'getParticipantGroups'
        }),
        ...mapState([
            'connected'
        ]),
        tweets() { return this.posts.filter(p => p.type == 'twitter' && !p.queued && !p.parent_post_id) },
        facebook_posts() { return this.posts.filter(p => p.type == 'facebook' && !p.queued && !p.parent_post_id) },
        emails() { return this.posts.filter(p => p.type == 'email' && !p.queued && !p.parent_post_id) },
    },
    mounted() {
        this.setTab('twitter')
        this.unwatch = this.$store.watch(
            (state, getters) => getters.getPubPosts,
            () => {
                this.setTab(this.tab)
            },
        );
    },
    beforeDestroy() {
        this.unwatch();
    },
    methods: {
        replyFilter(type) {
            return this.posts.filter(p => p.type === type && !p.queued && !this.viewed.includes(p.post_id))
        },
        unviewed(post_type) {
            return this.replyFilter(post_type).length
        },
        refreshGenUser() {
            this.$refs.postCompose.queueUser()
        },
        selectedAccount(obj) {
            this.selected_account = obj
        },
        replyPost(post_id, author_handle) {
            this.$refs.postCompose.replyTo(post_id, author_handle)
        },
        setTab(tab) {
            this.viewed.push(...this.replyFilter(tab).map(p => p.post_id))
            this.tab = tab
        },
        removePost(post_id) {
            this.$modal.show('dialog', {
                text: 'Er du sikker på at du vil slette dette?',
                buttons: [
                    {
                        title: '<b style="color: red">Ja</b>',
                        handler: () => {
                            this.$socket.emit('removePost', { post_id: post_id, instance_id: this.instance_id })
                            this.$modal.hide('dialog')
                        }
                    },
                    { title: 'Nej' }
                ]
            })
        },
    }
}
</script>